/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component } from 'react';
import { Box, Grid, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material';
import {
	ClientComment,
	ClientPost,
	ClientUserGeneratedContentFlagStatusRecord
} from '@DigitaleDoerfer/digitale-doerfer-api';
import FlaggedContentStatusHistoryView from './shared/FlaggedContentStatusHistory.view';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { flaggedContentService, flaggedContentViewService, timeService } from '../../../ServiceFactory';
import FlaggedContentCommentsView from './shared/FlaggedContentComments.view';
import clsx from 'clsx';
import { ClientPostBaseEntity } from '../services/FlaggedContent.service';
import { TimestampFormat } from '../../../shared/services/Time.service';
import FlaggedContentDetailsReasonView from './shared/FlaggedContentDetailsReason.view';
import FlaggedContentDetailsStatusView from './shared/FlaggedContentDetailsStatus.view';
import { commonStyles, FlagSummary } from './shared/FlaggedContentDetailsCommons';
import AvatarView from '../../../shared/views/Avatar.view';
import ImageGallery from '../../../shared/views/ImageGallery.view';
import { ReadOnlyTextFieldView } from '../../../shared/views/ReadOnlyTextField.view';
import FlaggedContentDetailsUrlView from './shared/FlaggedContentDetailsUrl.view';

interface Props extends WithStyles<typeof commonStyles> {
	flagPost?: ClientPost;
	flagPostDeleted?: boolean;
	flagSummary: FlagSummary;
	statusRecords: ClientUserGeneratedContentFlagStatusRecord[];
	commentsOfPost: ClientComment[];
}

class FlaggedPostView extends Component<Props> {
	renderFlaggedEntityInformation(): JSX.Element {
		const { flagSummary, flagPost } = this.props;
		const flaggedTextTitle = `${
			flagPost?.type && flaggedContentViewService().getPostTypeDisplayname(flagPost.type)
		} in ${flaggedContentViewService().renderInfo(flagSummary.tenantName)}`;

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<ReadOnlyTextFieldView
						id="text"
						name="text"
						label={flaggedTextTitle}
						multiline
						maxRows="5"
						value={flaggedContentViewService().renderInfo(flagSummary.text)}
						InputProps={{ readOnly: true }}
						InputLabelProps={{
							shrink: true
						}}
						fullWidth
					/>
				</Grid>
				<FlaggedContentDetailsReasonView reason={flagSummary.reason} />
				<FlaggedContentDetailsStatusView status={flagSummary.status} lastStatusUpdate={flagSummary.lastStatusUpdate} />
				<FlaggedContentDetailsUrlView flagPost={flagPost} />
			</Grid>
		);
	}

	renderRelatedPostInfo(tenantName?: string, clientPostBaseEntity?: ClientPostBaseEntity): JSX.Element {
		const { flagPostDeleted, classes } = this.props;
		if (!clientPostBaseEntity) {
			return <>Kein gültiger Beitrag gefunden</>;
		}

		return (
			<>
				<ListItem className={clsx(classes.listItem, classes.highlight)} alignItems="flex-start">
					<ListItemAvatar>
						<AvatarView
							avatarPicture={clientPostBaseEntity.creatorAvatarPicture}
							deleted={clientPostBaseEntity.creatorDeleted}
						/>
					</ListItemAvatar>
					<ListItemText
						primary={
							<>
								<Typography component="span" variant="h6" color="textPrimary">
									{`${clientPostBaseEntity.creatorName} - ${
										clientPostBaseEntity.created
											? timeService().parseTimestamp(clientPostBaseEntity.created, TimestampFormat.DD_MM_YYYY_HH_MM)
											: ''
									} - ${flaggedContentViewService().getPostTypeDisplayname(
										clientPostBaseEntity.type
									)} aus ${flaggedContentViewService().renderInfo(tenantName)}`}
								</Typography>
							</>
						}
						secondary={
							<>
								<Typography
									component="span"
									variant="h6"
									color="textPrimary"
									className={clsx(classes.textWrap, flagPostDeleted ? classes.textMarkedAsDeleted : undefined)}
								>
									{clientPostBaseEntity.text}
								</Typography>
								{flagPostDeleted && (
									<>
										<br />
										<Typography
											component="span"
											variant="h6"
											color="textPrimary"
											className={classes.deletionTextIndicator}
										>
											Dieser Post wurde gelöscht.
										</Typography>
									</>
								)}
							</>
						}
					/>
				</ListItem>
				<ImageGallery leftGalleryBoxPadding={'50px'} images={clientPostBaseEntity.images ?? []} />
			</>
		);
	}

	renderReportedContent(): JSX.Element | null {
		const { classes, commentsOfPost, flagSummary, flagPost } = this.props;
		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="h2" gutterBottom className={classes.typographyTitle}>
						Gesprächsverlauf
					</Typography>
				</Grid>
				<Grid item xs={12} className={classes.gridFirstItem}>
					{this.renderRelatedPostInfo(
						flagSummary.tenantName,
						flaggedContentService().getClientPostBaseEntityFromPost(flagPost)
					)}
					{commentsOfPost && commentsOfPost.length > 0 ? (
						<Box pt={3}>
							<Typography variant="h6" gutterBottom>
								Kommentare:
							</Typography>
						</Box>
					) : (
						''
					)}
					<FlaggedContentCommentsView commentsOfPost={commentsOfPost} highlightedCommentId={''} />
				</Grid>
			</Grid>
		);
	}

	render(): JSX.Element {
		const { flagSummary, statusRecords } = this.props;
		return (
			<>
				{this.renderFlaggedEntityInformation()}
				{this.renderReportedContent()}
				<FlaggedContentStatusHistoryView flagSummary={flagSummary} statusRecords={statusRecords} />
			</>
		);
	}
}

export default withStyles(commonStyles)(FlaggedPostView);
